/**
 * Service for exporting data to various formats
 */

import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import type { InsightPdfItem } from '../types/insights';
import { PreferencesService, SpreadsheetFormat } from './preferencesService';

export interface ExportData {
  [key: string]: any;
}

export interface ExportOptions {
  filename: string;
  sheetName?: string;
  format?: SpreadsheetFormat;
  includeDateInFilename?: boolean;
}

export interface PDFExportOptions {
  filename: string;
  title: string;
  content?: {
    type: 'chart' | 'table' | 'mixed';
    chartElement?: HTMLElement;
    tableData?: any[];
    tableColumns?: { header: string; dataKey: string }[];
  };
  includeDateInFilename?: boolean;
}

/**
 * Get user's preferred spreadsheet format
 */
function getSpreadsheetFormat(): SpreadsheetFormat {
  const preferences = PreferencesService.getPreferences();
  return preferences.spreadsheetFormat || 'excel';
}

/**
 * Get background color based on current theme
 * Optionally accepts an element to get its computed background color
 */
function getThemeBackgroundColor(element?: HTMLElement | null): string {
  // First, try to get the background color from the specific element if provided
  if (element) {
    try {
      const computedStyle = window.getComputedStyle(element);
      const backgroundColor = computedStyle.backgroundColor;
      
      // If we get a valid color (not transparent), use it
      if (backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'transparent') {
        return backgroundColor;
      }
      
      // Try to get background from parent if element itself is transparent
      const parent = element.parentElement;
      if (parent) {
        const parentStyle = window.getComputedStyle(parent);
        const parentBg = parentStyle.backgroundColor;
        if (parentBg && parentBg !== 'rgba(0, 0, 0, 0)' && parentBg !== 'transparent') {
          return parentBg;
        }
      }
    } catch (e) {
      // Continue to fallback
    }
  }
  
  // Try to get the computed background color from the body element
  try {
    const bodyElement = document.body;
    const computedStyle = window.getComputedStyle(bodyElement);
    const backgroundColor = computedStyle.backgroundColor;
    
    // If we get a valid color (not transparent), use it
    if (backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'transparent') {
      return backgroundColor;
    }
  } catch (e) {
    // Continue to fallback
  }
  
  // Check if dark mode is active by looking for the 'dark' class on document.documentElement
  const isDarkMode = document.documentElement.classList.contains('dark');
  
  // Fallback to default colors based on dark mode
  return isDarkMode ? '#1a1a1a' : '#ffffff';
}

/**
 * Sanitize filename by removing invalid characters
 */
function sanitizeFilename(filename: string): string {
  return filename.replace(/[<>:"/\\|?*]/g, '_').trim();
}

/**
 * Generate filename with optional date suffix
 */
function generateFilename(baseName: string, extension: string, includeDate: boolean = true): string {
  let filename = sanitizeFilename(baseName);
  if (includeDate) {
    const dateStr = new Date().toISOString().split('T')[0];
    filename = `${filename}-${dateStr}`;
  }
  return `${filename}.${extension}`;
}

/**
 * Convert Excel workbook to CSV format
 */
function workbookToCSV(wb: XLSX.WorkBook, sheetName?: string): string {
  const ws = sheetName ? wb.Sheets[sheetName] : wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_csv(ws);
}

/**
 * Export data to spreadsheet format based on user preference
 */
export function exportToSpreadsheet(
  data: ExportData[],
  options: ExportOptions
): void {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  const format = options.format || getSpreadsheetFormat();
  const sheetName = options.sheetName || 'Sheet1';
  const includeDate = options.includeDateInFilename !== false;

  try {
    // Convert data to worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Handle different formats
    switch (format) {
      case 'csv':
      case 'google-sheets':
        // For CSV and Google Sheets, export as CSV
        const csvContent = workbookToCSV(wb, sheetName);
                      const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
              const csvFilename = generateFilename(options.filename, 'csv', includeDate);
              FileSaver.saveAs(csvBlob, csvFilename);
        break;

      case 'numbers':
      case 'excel':
      default:
        // Numbers can open Excel files, so export as .xlsx for both
        const excelFilename = generateFilename(options.filename, 'xlsx', includeDate);
        XLSX.writeFile(wb, excelFilename);
        break;
    }
  } catch (error) {
    console.error('Error exporting to spreadsheet:', error);
    throw new Error('Failed to export data. Please try again.');
  }
}

/**
 * Export multiple sheets to Excel workbook
 */
export function exportToMultiSheetSpreadsheet(
  sheets: { name: string; data: ExportData[] }[],
  options: Omit<ExportOptions, 'sheetName'>
): void {
  if (!sheets || sheets.length === 0) {
    throw new Error('No sheets to export');
  }

  // Multi-sheet exports always use Excel format
  const includeDate = options.includeDateInFilename !== false;

  try {
    const wb = XLSX.utils.book_new();

    sheets.forEach((sheet) => {
      if (sheet.data && sheet.data.length > 0) {
        const ws = XLSX.utils.json_to_sheet(sheet.data);
        // Sanitize sheet name (Excel has limitations)
        // eslint-disable-next-line no-useless-escape
        const safeSheetName = sheet.name.replace(/[\\/?*\[\]]/g, '_').substring(0, 31);
        XLSX.utils.book_append_sheet(wb, ws, safeSheetName);
      }
    });

    const filename = generateFilename(options.filename, 'xlsx', includeDate);
    XLSX.writeFile(wb, filename);
  } catch (error) {
    console.error('Error exporting multi-sheet spreadsheet:', error);
    throw new Error('Failed to export data. Please try again.');
  }
}

/**
 * Export chart/analysis to PDF
 */
export async function exportToPDF(options: PDFExportOptions): Promise<void> {
  const { filename, title, content, includeDateInFilename = true } = options;

  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;

    // Add title
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, margin, margin + 10);

    let yPosition = margin + 20;

    if (content) {
      if (content.type === 'chart' && content.chartElement) {
        // Capture chart as image
        const canvas = await html2canvas(content.chartElement, {
          scale: 2,
          logging: false,
          useCORS: true,
          backgroundColor: getThemeBackgroundColor(content.chartElement),
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Check if we need a new page
        if (yPosition + imgHeight > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight + 10;

        // Add data table below chart if provided
        if (content.tableData && content.tableData.length > 0 && content.tableColumns) {
          if (yPosition > pageHeight - margin - 50) {
            pdf.addPage();
            yPosition = margin;
          }

          autoTable(pdf, {
            startY: yPosition,
            head: [content.tableColumns.map(col => col.header)],
            body: content.tableData.map(row =>
              content.tableColumns!.map(col => row[col.dataKey]?.toString() || '')
            ),
            margin: { left: margin, right: margin },
            styles: { fontSize: 8 },
            headStyles: { fillColor: [66, 139, 202] },
          });
        }
      } else if (content.type === 'table' && content.tableData && content.tableColumns) {
        // Export table only
        autoTable(pdf, {
          startY: yPosition,
          head: [content.tableColumns.map(col => col.header)],
          body: content.tableData.map(row =>
            content.tableColumns!.map(col => row[col.dataKey]?.toString() || '')
          ),
          margin: { left: margin, right: margin },
          styles: { fontSize: 9 },
          headStyles: { fillColor: [66, 139, 202] },
        });
      }
    }

    // Save PDF
    const pdfFilename = generateFilename(filename, 'pdf', includeDateInFilename);
    pdf.save(pdfFilename);
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw new Error('Failed to export PDF. Please try again.');
  }
}

/**
 * Export multiple charts/analyses to a single PDF (multi-page)
 */
export async function exportMultipleToPDF(
  pages: Array<{
    title: string;
    chartElement?: HTMLElement;
    tableData?: any[];
    tableColumns?: { header: string; dataKey: string }[];
  }>,
  filename: string,
  includeDateInFilename: boolean = true
): Promise<void> {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];
      
      // Add new page for each item (except first)
      if (i > 0) {
        pdf.addPage();
      }

      let yPosition = margin;

      // Add title
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text(page.title, margin, yPosition + 10);
      yPosition += 20;

      // Add chart if provided
      if (page.chartElement) {
        const canvas = await html2canvas(page.chartElement, {
          scale: 2,
          logging: false,
          useCORS: true,
          backgroundColor: getThemeBackgroundColor(page.chartElement),
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (yPosition + imgHeight > pageHeight - margin) {
          // Chart too large, scale it down
          const maxHeight = pageHeight - margin - yPosition - 20;
          const scaledWidth = (imgWidth * maxHeight) / imgHeight;
          const scaledHeight = maxHeight;
          pdf.addImage(imgData, 'PNG', margin, yPosition, scaledWidth, scaledHeight);
          yPosition += scaledHeight + 10;
        } else {
          pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 10;
        }
      }

      // Add table if provided
      if (page.tableData && page.tableData.length > 0 && page.tableColumns) {
        if (yPosition > pageHeight - margin - 50) {
          pdf.addPage();
          yPosition = margin;
        }

        autoTable(pdf, {
          startY: yPosition,
          head: [page.tableColumns.map(col => col.header)],
          body: page.tableData.map(row =>
            page.tableColumns!.map(col => row[col.dataKey]?.toString() || '')
          ),
          margin: { left: margin, right: margin },
          styles: { fontSize: 8 },
          headStyles: { fillColor: [66, 139, 202] },
        });
      }
    }

    const pdfFilename = generateFilename(filename, 'pdf', includeDateInFilename);
    pdf.save(pdfFilename);
  } catch (error) {
    console.error('Error exporting multiple items to PDF:', error);
    throw new Error('Failed to export PDF. Please try again.');
  }
}

/**
 * Build a text-only PDF (for serverless email attachments) from insight titles and action lists.
 * Returns raw PDF bytes without triggering a browser download.
 */
export async function buildInsightsPdfBlob(
  title: string,
  items: InsightPdfItem[]
): Promise<Uint8Array> {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 18;
  let y = margin;

  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title, margin, y);
  y += 10;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);

  const addParagraph = (text: string, indent = 0) => {
    const lines = pdf.splitTextToSize(text, pageWidth - margin * 2 - indent);
    for (const line of lines) {
      if (y > pdf.internal.pageSize.getHeight() - margin) {
        pdf.addPage();
        y = margin;
      }
      pdf.text(line, margin + indent, y);
      y += 5;
    }
  };

  if (!items.length) {
    addParagraph('No insights were generated for this run.');
  }

  for (const item of items) {
    if (y > pdf.internal.pageSize.getHeight() - margin - 24) {
      pdf.addPage();
      y = margin;
    }
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    addParagraph(item.title);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    addParagraph(item.summary, 2);
    if (item.actions?.length) {
      addParagraph('Suggested actions:', 2);
      item.actions.forEach((a, i) => {
        addParagraph(`${i + 1}. ${a}`, 4);
      });
    }
    y += 4;
  }

  const out = pdf.output('arraybuffer');
  return new Uint8Array(out);
}

/**
 * Format data value for export (handles dates, numbers, etc.)
 */
export function formatExportValue(value: any): any {
  if (value === null || value === undefined) {
    return '';
  }

  if (value instanceof Date) {
    return value.toLocaleDateString();
  }

  if (typeof value === 'number') {
    return value;
  }

  return String(value);
}
